import moment from 'moment';
import { DateRangeFilterConfig } from './filter-options-config.service';

const UTC_EDP_OUTPUT_FORMAT = 'YYYY-MM-DDTHH:mm:ss[Z]';

export enum DateRangeFilterOptions {
  last3Days = 'last3Days',
  last7Days = 'last7Days',
  last2Weeks = 'last2Weeks',
  last1Month = 'last1Month',
  last3Month = 'last3Month',
  last6Month = 'last6Month',
  last1Year = 'last1Year',
  last18Months = 'last18Months',
  last2Years = 'last2Years',
  next1Year = 'next1Year',
  next18Years = 'next18Years',
  next2Years = 'next2Years',
}

export interface DateBySelectedOption {
  from: string;
  to: string;
}

export interface DateRangeFilterOffsetObj {
  year: number;
  month: number;
  day?: number;
}

export interface DateRangeOptionItem {
  label: string;
  key: DateRangeFilterOptions;
}

export const RECORD_SEARCH_OPTIONS: DateRangeOptionItem[] = [
  { label: 'recordsSearch.last1Year', key: DateRangeFilterOptions.last1Year },
  { label: 'recordsSearch.last18Months', key: DateRangeFilterOptions.last18Months },
  { label: 'recordsSearch.last2Years', key: DateRangeFilterOptions.last2Years },
  { label: 'recordsSearch.next1Year', key: DateRangeFilterOptions.next1Year },
  { label: 'recordsSearch.next18Years', key: DateRangeFilterOptions.next18Years },
  { label: 'recordsSearch.next2Years', key: DateRangeFilterOptions.next2Years },
];

export const REPORTS_OPTIONS: DateRangeOptionItem[] = [
  { label: 'reports.lastThreeDays', key: DateRangeFilterOptions.last3Days },
  { label: 'reports.lastSevenDays', key: DateRangeFilterOptions.last7Days },
  { label: 'reports.lastTwoWeeks', key: DateRangeFilterOptions.last2Weeks },
  { label: 'reports.lastMonth', key: DateRangeFilterOptions.last1Month },
  { label: 'reports.lastThreeMonths', key: DateRangeFilterOptions.last3Month },
  { label: 'reports.lastSixMonths', key: DateRangeFilterOptions.last6Month },
  { label: 'reports.last12Months', key: DateRangeFilterOptions.last1Year },
];

/**
 * Factory function to create DateRangeFilterConfig with translation service.
 * Resolves translation keys at runtime.
 */
export const createDateRangeFilterConfig = (
  translateFn: (key: string) => string,
  recordSearchOptions: DateRangeOptionItem[] = RECORD_SEARCH_OPTIONS,
  reportsOptions: DateRangeOptionItem[] = REPORTS_OPTIONS
): DateRangeFilterConfig => {
  return Object.freeze({
    getOptionsForRecordSearch: () => recordSearchOptions.map(({ label, key }) => ({ label: translateFn(label), key })),
    getOptionsForReports: () => reportsOptions.map(({ label, key }) => ({ label: translateFn(label), key })),
  });
};

export const DATE_RANGE_FILTER_REPORTS_OFFSET: { [key: string]: { [key: string]: DateRangeFilterOffsetObj } } = {
  [DateRangeFilterOptions.last3Days]: {
    from: { year: 0, month: 0, day: -3 },
    to: { year: 0, month: 0 },
  },
  [DateRangeFilterOptions.last7Days]: {
    from: { year: 0, month: 0, day: -7 },
    to: { year: 0, month: 0 },
  },
  [DateRangeFilterOptions.last2Weeks]: {
    from: { year: 0, month: 0, day: -14 },
    to: { year: 0, month: 0 },
  },
  [DateRangeFilterOptions.last1Month]: {
    from: { year: 0, month: -1 },
    to: { year: 0, month: 0 },
  },

  [DateRangeFilterOptions.last3Month]: {
    from: { year: 0, month: -3 },
    to: { year: 0, month: 0 },
  },
  [DateRangeFilterOptions.last6Month]: {
    from: { year: 0, month: -6 },
    to: { year: 0, month: 0 },
  },
  [DateRangeFilterOptions.last1Year]: {
    from: { year: -1, month: 0 },
    to: { year: 0, month: 0 },
  },
};

export const DATE_RANGE_FILTER_OFFSET: { [key: string]: { [key: string]: DateRangeFilterOffsetObj } } = {
  [DateRangeFilterOptions.last1Year]: {
    from: { year: -1, month: 0 },
    to: { year: 0, month: 0 },
  },
  [DateRangeFilterOptions.last18Months]: {
    from: { year: 0, month: -18 },
    to: { year: 0, month: 0 },
  },
  [DateRangeFilterOptions.last2Years]: {
    from: { year: -2, month: 0 },
    to: { year: 0, month: 0 },
  },
  [DateRangeFilterOptions.next1Year]: {
    from: { year: 0, month: 0 },
    to: { year: 1, month: 0 },
  },
  [DateRangeFilterOptions.next18Years]: {
    from: { year: 0, month: 0 },
    to: { year: 0, month: 18 },
  },
  [DateRangeFilterOptions.next2Years]: {
    from: { year: 0, month: 0 },
    to: { year: 2, month: 0 },
  },
};

/**
 * Get range of dates according to the selected period to filter
 * @param selectedOpt DateRangeFilterOptions
 * @returns
 */
export const getDatesBySelectedOption = (
  selectedOpt: DateRangeFilterOptions,
  isReports = false
): DateBySelectedOption => {
  const offset = isReports ? DATE_RANGE_FILTER_REPORTS_OFFSET : DATE_RANGE_FILTER_OFFSET;
  const from = getDateForSelectedOption(offset[selectedOpt].from, 'startOf');
  const to = getDateForSelectedOption(offset[selectedOpt].to, 'endOf');
  return { from, to };
};

const getDateForSelectedOption = (dateOffset: DateRangeFilterOffsetObj, dateTime: 'endOf' | 'startOf') => {
  return moment()
    .add(dateOffset.year, 'year')
    .add(dateOffset.month, 'month')
    .add(dateOffset.day, 'day')
    [dateTime]('day')
    .format(UTC_EDP_OUTPUT_FORMAT);
};
